import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { Popover as PopoverPrimitive } from "@kobalte/core";
import { Monitor, Moon, Palette, Sparkles, Sun } from "@fleet/icons";
import { cn } from "@journal/shared";

import { useNavigate } from "@tanstack/solid-router";
import { type ColorMode, useTheme } from "@/providers/theme-provider";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Single appearance control in the header: color mode (System / Light /
 * Dark), blur effect, and access to the theme gallery.
 *
 * Before, there were THREE loose buttons in the upper right —blur, appearance,
 * and palette—for what is actually one family of settings. They are combined
 * into one, leaving two gaps for other kinds of utilities.
 *
 * Why this survives instead of the shortcut to the gallery, which was the alternative:
 * color mode changes several times a day and **works without a session**, while
 * `/themes` gates custom themes behind login. With only the link, a guest could not
 * switch to dark mode; and the most frequent action would cost a navigation and a
 * return trip. The reverse is free: choosing a theme is rare and deliberate, so one
 * extra click does not matter.
 */
export function AppearanceMenu() {
  const theme = useTheme();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = createSignal(false);

  const choose = (mode: ColorMode) => {
    theme.selectColorMode(mode);
    setOpen(false);
  };

  const options: { mode: ColorMode; label: MessageDescriptor; icon: typeof Sun }[] = [
    { mode: "system", label: msg`System`, icon: Monitor },
    { mode: "light", label: msg`Light`, icon: Sun },
    { mode: "dark", label: msg`Dark`, icon: Moon },
  ];

  return (
    <PopoverPrimitive.Root open={open()} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger
        as="button"
        type="button"
        class={cn(
          "inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-primary/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
        title={t(msg`Appearance`)}
        aria-label={t(msg`Appearance`)}
      >
        <Show when={theme.isDark()} fallback={<Sun class="h-4 w-4" />}>
          <Moon class="h-4 w-4" />
        </Show>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          data-testid="appearance-menu"
          class="z-50 w-52 rounded-md border border-border bg-popover p-1.5 text-popover-foreground shadow-md outline-none animate-in fade-in zoom-in-95 duration-200"
        >
          <p class="px-2 py-1 text-xs font-semibold text-muted-foreground">{t(msg`Appearance`)}</p>
          <For each={options}>
            {(opt) => (
              <button
                type="button"
                data-testid={`appearance-mode-${opt.mode}`}
                class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent/50 data-[active=true]:bg-accent/40 data-[active=true]:font-semibold"
                data-active={theme.colorMode() === opt.mode}
                onClick={() => choose(opt.mode)}
              >
                <opt.icon class="h-4 w-4" />
                <span>{t(opt.label)}</span>
              </button>
            )}
          </For>

          <div class="my-1 h-px bg-border" />

          {/* Blur used to be the third button in the header. This explains what it does
              —como icono suelto no se sabía— y sigue mostrando su estado. */}
          <button
            type="button"
            data-testid="appearance-blur"
            role="switch"
            aria-checked={theme.isBlur()}
            class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent/50"
            onClick={() => {
              void theme.toggleBlur();
            }}
          >
            <Sparkles class={cn("h-4 w-4", theme.isBlur() && "text-primary")} />
            <span class="flex-1 text-left">{t(msg`Blur effect`)}</span>
            {/* State is conveyed by the icon (color) and by `aria-checked`,
                no con un "On/Off" escrito. No es cosmético: cualquier cadena nueva
                saldría EN INGLÉS en los once idiomas, porque el gate de i18n solo
                valida lo que ya está en el catálogo y no obliga a extraer. Aquí
                todas las etiquetas existen y están traducidas. */}
            <span
              aria-hidden="true"
              class={cn(
                "h-2 w-2 rounded-full",
                theme.isBlur() ? "bg-primary" : "bg-muted-foreground/30",
              )}
            />
          </button>

          {/* Theme gallery: it used to be the palette button. It remains the last
              elemento porque es la única entrada que NAVEGA — el resto se aplica
              en el sitio y deja el menú donde estaba. */}
          <button
            type="button"
            data-testid="appearance-customize"
            class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent/50"
            onClick={() => {
              setOpen(false);
              void navigate({ to: "/themes" });
            }}
          >
            <Palette class="h-4 w-4" />
            {/* "Temas" instead of "Personalizar": it is the same destination and is already
                traducida en los once idiomas. Una cadena nueva aquí se vería en
                inglés hasta que alguien haga una pasada de i18n. */}
            <span>{t(msg`Themes`)}</span>
          </button>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
